import { isPromptDismissed } from './session-dismissals.js';
import { HttpRequestError } from '../shared/request-error.js';
import { safeLocalStorageGet, safeLocalStorageSet } from './safe-storage.js';
import { isSatelliteBasemap } from '../shared/satellite-access.js';
import { DEFAULT_BASEMAP_ID } from './map-shared.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { isString } from '../shared/runtime-values.js';
import { configureAzureSatelliteTileUrl } from './map-shared.js';
import { monitorSatelliteTiles } from './satellite-tile-monitor.js';
import type { MapAnalyticsTrack } from '../shared/analytics-schema.js';
import type TileSource from 'ol/source/Tile.js';
import { SATELLITE_PRO_GRANT_REFRESH_SECONDS } from '../shared/satellite-access.js';

const CHECKOUT_BASEMAP_KEY = 'drawonamap.satelliteCheckoutBasemap';

export function getSatelliteCheckoutBasemap(): 'satellite' | 'hybrid' {
  return safeLocalStorageGet(CHECKOUT_BASEMAP_KEY) === 'hybrid' ? 'hybrid' : 'satellite';
}

type SatelliteGrant =
  | { status: 'empty' }
  | { status: 'requesting' | 'ready'; request: Promise<string>; refreshAt: number };
let proAccess: SatelliteGrant = { status: 'empty' };
export const SATELLITE_GRANT_REFRESH_MS = SATELLITE_PRO_GRANT_REFRESH_SECONDS * 1_000;
const SATELLITE_GRANT_RETRY_MS = 60 * 1_000;

function requestAzureSatelliteAccess(fetchImpl: typeof fetch): Promise<string> {
  return fetchImpl('/api/satellite-access', { credentials: 'same-origin' })
    .then(async (response) => {
      // SAFETY: The following parser checks every field that it accepts from this external payload.
      const payload = await response.json().catch(() => null) as { tile_url?: unknown; error?: unknown } | null;
      if (!response.ok) {
        throw new HttpRequestError(isString(payload?.error) ? payload.error : `Azure Maps access failed: ${response.status}`, response, 'satellite.access');
      }
      if (!isString(payload?.tile_url) || !payload.tile_url.startsWith('/api/azure-satellite?')) {
        throw new Error('Azure Maps access response was invalid');
      }
      configureAzureSatelliteTileUrl(payload.tile_url);
      return payload.tile_url;
    });
}

export async function ensureAzureSatelliteAccess(
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  if (proAccess.status === 'ready' && Date.now() >= proAccess.refreshAt) proAccess = { status: 'empty' };
  if (proAccess.status === 'empty') {
    const requestedAt = Date.now();
    const request = requestAzureSatelliteAccess(fetchImpl);
    proAccess = { status: 'requesting', request, refreshAt: requestedAt + SATELLITE_GRANT_REFRESH_MS };
    void request.then(
      () => {
        if (proAccess.status === 'empty' || proAccess.request !== request) return;
        proAccess = { ...proAccess, status: 'ready' };
      },
      () => {
        if (proAccess.status === 'empty' || proAccess.request !== request) return;
        proAccess = { status: 'empty' };
      },
    );
  }
  return proAccess.request;
}

export async function renewAzureSatelliteAccess(fetchImpl: typeof fetch = fetch): Promise<string> {
  if (proAccess.status === 'requesting') return proAccess.request;
  proAccess = { status: 'empty' };
  return ensureAzureSatelliteAccess(fetchImpl);
}

interface SatelliteControllerDeps {
  state: { currentUser: unknown; pendingSatelliteBasemap: boolean | 'satellite' | 'hybrid'; currentBasemap: string };
  mapLayers: { onBasemapSelected(listener: (basemapId: string) => void): void };
  isProUser(user: SchemaInput): boolean;
  getUserPlan(): string;
  setBasemap(id: string, options?: { syncUrl?: boolean }): TileSource | TileSource[] | null | void;
  normalizeBasemapId(id: string): string;
  openUpgrade(): void;
  showUnavailable(): void;
  showUpgradeReminder(onUpgrade: () => void): void;
  track: MapAnalyticsTrack;
  trackStyleAction(): void;
  onSelected?(before: string, after: string): void;
}

export function createSatelliteBasemapController(deps: SatelliteControllerDeps) {
  type Selection = { status: 'idle' } | { status: 'selecting'; basemapId: string };
  type SatelliteSession = { status: 'inactive' } | {
    status: 'active'; source: TileSource; stopMonitor(): void; timer: ReturnType<typeof setTimeout> | null;
  };
  let selection: Selection = { status: 'idle' };
  let session: SatelliteSession = { status: 'inactive' };

  function stopSatelliteSession(): void {
    const previous = session;
    session = { status: 'inactive' };
    if (previous.status !== 'active') return;
    previous.stopMonitor();
    if (previous.timer !== null) clearTimeout(previous.timer);
  }

  function scheduleGrantRefresh(active: Extract<SatelliteSession, { status: 'active' }>, delay = SATELLITE_GRANT_REFRESH_MS): void {
    if (active.timer !== null) clearTimeout(active.timer);
    active.timer = setTimeout(() => {
      active.timer = null;
      if (session !== active) return;
      void renewAzureSatelliteAccess().then(
        () => { if (session === active) scheduleGrantRefresh(active); },
        () => { if (session === active) scheduleGrantRefresh(active, SATELLITE_GRANT_RETRY_MS); },
      );
    }, delay);
  }

  function applyBasemap(basemapId: string, options?: { syncUrl?: boolean }): void {
    selection = { status: 'idle' };
    stopSatelliteSession();
    const result = deps.setBasemap(basemapId, options);
    const sources = Array.isArray(result) ? result : result ? [result] : [];
    const source = sources[0];
    if (!isSatelliteBasemap(basemapId) || !source) return;
    const renewable = deps.isProUser(deps.state.currentUser);
    const stopMonitor = monitorSatelliteTiles(sources.map((source, index) => ({ source, layer: index === 0 ? 'imagery' : 'labels' })), deps.track, {
      basemap: basemapId,
      reportFailure: renewable,
      recover: renewable ? async () => {
        await renewAzureSatelliteAccess();
        if (session === active) sources.forEach(item => item.refresh());
      } : undefined,
    });
    const active: Extract<SatelliteSession, { status: 'active' }> = { status: 'active', source, stopMonitor, timer: null };
    session = active;
    if (renewable) scheduleGrantRefresh(active);
  }

  async function select(requestedBasemapId: string, source: 'user' | 'restore' = 'user'): Promise<void> {
    const basemapId = deps.normalizeBasemapId(requestedBasemapId);
    const request: Selection = { status: 'selecting', basemapId };
    selection = request;
    if (source === 'user') deps.track('map_style_clicked', { tool: basemapId, user_plan: deps.getUserPlan() });
    if (isSatelliteBasemap(basemapId)) {
      if (!deps.isProUser(deps.state.currentUser)) {
        selection = { status: 'idle' };
        safeLocalStorageSet(CHECKOUT_BASEMAP_KEY, basemapId);
        if (isPromptDismissed('satellite-upgrade')) deps.showUpgradeReminder(() => deps.openUpgrade());
        else deps.openUpgrade();
        return;
      }
      try { await ensureAzureSatelliteAccess(); }
      catch (error) {
        if (selection !== request) return;
        selection = { status: 'idle' };
        console.warn('Satellite map access failed:', error); deps.showUnavailable(); return;
      }
    }
    if (selection !== request) return;
    const previousBasemap = deps.state.currentBasemap;
    applyBasemap(basemapId);
    if (source === 'user') {
      deps.onSelected?.(previousBasemap, basemapId);
      deps.track('map_style_selected', { tool: basemapId, user_plan: deps.getUserPlan() });
      deps.trackStyleAction();
    }
  }

  deps.mapLayers.onBasemapSelected((basemapId) => { void select(basemapId); });

  async function restoreShared(basemapId: string): Promise<void> {
    const normalized = deps.normalizeBasemapId(basemapId);
    const request: Selection = { status: 'selecting', basemapId: normalized };
    selection = request;
    let allowed = normalized;
    if (isSatelliteBasemap(normalized)) {
      if (!deps.isProUser(deps.state.currentUser)) allowed = DEFAULT_BASEMAP_ID;
      else {
        try { await ensureAzureSatelliteAccess(); }
        catch { if (selection !== request) return; allowed = DEFAULT_BASEMAP_ID; deps.showUnavailable(); }
      }
    }
    if (selection === request) applyBasemap(allowed, { syncUrl: false });
  }

  async function restorePending(): Promise<void> {
    const pending = deps.state.pendingSatelliteBasemap;
    if (!pending || !deps.isProUser(deps.state.currentUser)) return;
    deps.state.pendingSatelliteBasemap = false;
    const request: Selection = { status: 'selecting', basemapId: pending === 'hybrid' ? 'hybrid' : 'satellite' };
    selection = request;
    try {
      await ensureAzureSatelliteAccess();
      if (selection === request) applyBasemap(request.basemapId, { syncUrl: false });
    } catch (error) {
      if (selection !== request) return;
      selection = { status: 'idle' };
      console.warn('Saved satellite map access failed:', error); deps.showUnavailable();
    }
  }

  return { restorePending, restoreShared, applyBasemap, select };
}
